import api from './api';
import type {
  DashboardCockpitAnalyticsSliceName,
  DashboardCockpitAnalyticsSliceResponse,
  DashboardCockpitResponse,
  DashboardCockpitTodayAiAgentResponse,
  DashboardCockpitTodayComparisonResponse,
  SystemDashboardCockpitResponse,
} from '../types';

/**
 * Load the canonical snapshot-first cockpit payload for the dashboard.
 */
export async function fetchDashboardCockpit(historyDays = 30): Promise<DashboardCockpitResponse> {
  const response = await api.get('/api/dashboard/cockpit/', {
    params: {
      history_days: historyDays,
    },
  });
  return response.data as DashboardCockpitResponse;
}

/**
 * Load one cached historical analytics slice for the dashboard cockpit.
 */
export async function fetchDashboardCockpitAnalyticsSlice(
  sliceName: DashboardCockpitAnalyticsSliceName,
  historyDays = 30,
): Promise<DashboardCockpitAnalyticsSliceResponse> {
  const response = await api.get(`/api/dashboard/cockpit/analytics/${sliceName}/`, {
    params: {
      history_days: historyDays,
    },
  });
  return response.data as DashboardCockpitAnalyticsSliceResponse;
}

/**
 * Load the lazy same-elapsed-day comparison payload for Today panels.
 */
export async function fetchDashboardCockpitTodayComparison(
  historyDays = 30,
): Promise<DashboardCockpitTodayComparisonResponse> {
  const response = await api.get('/api/dashboard/cockpit/today-comparison/', {
    params: {
      history_days: historyDays,
    },
  });
  return response.data as DashboardCockpitTodayComparisonResponse;
}

/**
 * Load the lazy Today snapshot for one selected AI agent.
 */
export async function fetchDashboardCockpitTodayAiAgent(
  agentId: string,
  historyDays = 30,
): Promise<DashboardCockpitTodayAiAgentResponse> {
  const response = await api.get(`/api/dashboard/cockpit/today-ai-agents/${agentId}/`, {
    params: {
      history_days: historyDays,
    },
  });
  return response.data as DashboardCockpitTodayAiAgentResponse;
}

interface FetchSystemDashboardCockpitOptions {
  dateFrom?: string | null;
  dateTo?: string | null;
}

/**
 * Load the platform-wide cockpit payload used by superusers and system admins.
 */
export async function fetchSystemDashboardCockpit(
  options: FetchSystemDashboardCockpitOptions = {},
): Promise<SystemDashboardCockpitResponse> {
  const response = await api.get('/api/dashboard/system-cockpit/', {
    params: {
      date_from: options.dateFrom || undefined,
      date_to: options.dateTo || undefined,
    },
  });
  return response.data as SystemDashboardCockpitResponse;
}
